import React, { useMemo, useState, useEffect } from "react";
import {
  FaLayerGroup,
  FaNetworkWired,
  FaChartBar,
  FaWarehouse,
  FaCogs,
  FaBuilding,
  FaUser,
  FaTimes,
} from "react-icons/fa";
import {
  getDeviceDisplayName,
  getDeviceCupNumber,
  deviceHasGrinder,
  getCupFromDeviceLabel,
  formatCupLabel,
} from "../../utils/deviceUtils";
import MascotLoadingOverlay from "../shared/MascotLoadingOverlay";
import Drawer from "../shared/Drawer";
import useDrawerTransition from "../../Hooks/useDrawerTransition";

function parseDeviceLabel(deviceName) {
  const name = String(deviceName || "").trim();
  if (name.endsWith(" (Değirmenli)")) return { base: name.slice(0, -12).trim(), grinder: "Değirmenli" };
  if (name.endsWith(" (Değirmensiz)")) return { base: name.slice(0, -14).trim(), grinder: "" };
  return { base: name, grinder: null };
}

function normalizeInvalidDeviceLabel(label) {
  const map = { "5'li Fincanda": "5'li Çayset-Fincanda" };
  return map[label] || label;
}

// Üretim dağılıma dahil edilmez (kullanıcı kararı) — server da hariç tutar
const STATUS_ORDER = ["Depo", "Servis", "Firma", "Plasiyer"];
const STATUS_CONFIG = {
  Depo: { icon: FaWarehouse, color: "purple" },
  Servis: { icon: FaCogs, color: "teal" },
  Firma: { icon: FaBuilding, color: "indigo" },
  Plasiyer: { icon: FaUser, color: "blue" },
};

const STYLES = {
  purple: "bg-purple-50 border-purple-200 text-purple-700",
  green: "bg-green-50 border-green-200 text-green-700",
  teal: "bg-teal-50 border-teal-200 text-teal-700",
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  blue: "bg-blue-50 border-blue-200 text-blue-700",
};

const ICON_STYLES = {
  purple: "bg-purple-100 text-purple-600",
  green: "bg-green-100 text-green-600",
  teal: "bg-teal-100 text-teal-600",
  indigo: "bg-indigo-100 text-indigo-600",
  blue: "bg-blue-100 text-blue-600",
};

const BORDER_COLORS = {
  purple: "border-purple-200 bg-purple-50/30",
  green: "border-green-200 bg-green-50/30",
  teal: "border-teal-200 bg-teal-50/30",
  indigo: "border-indigo-200 bg-indigo-50/30",
  blue: "border-blue-200 bg-blue-50/30",
};

export default function DistributionDrawer({
  open,
  onClose,
  devicesData = [],
  totalDevices = 0,
  distributionData = null,
  distributionLoading = false,
}) {
  // Ortak Drawer çıkış animasyonunu oynatabilsin diye kapanışta 300 ms daha
  // monte kalınır (aksi halde bileşen anında unmount olup drawer "tak" diye
  // kayboluyordu).
  const { mounted } = useDrawerTransition(open);

  const [serialsModal, setSerialsModal] = useState(null);
  // Genel toplam filtreleri — model (3 model) / değirmen / göz sayısı
  const [totalsFilter, setTotalsFilter] = useState({ model: "all", grinder: "all", cup: "all" });
  const useDistribution = open && distributionData && !distributionLoading;
  const serialNumbersByType = distributionData?.serialNumbersByType || {};
  const serialNumbersByStatusAndType = distributionData?.serialNumbersByStatusAndType || {};

  useEffect(() => {
    if (!open) {
      setSerialsModal(null);
      setTotalsFilter({ model: "all", grinder: "all", cup: "all" });
    }
  }, [open]);

  const totalDevicesDisplay = useDistribution ? distributionData.totalDevices : totalDevices;
  const onlineCount = useDistribution ? 0 : devicesData.filter((d) => d.deviceInfo?.connection?.status)?.length || 0;
  const statusPercent = totalDevicesDisplay ? Math.round((onlineCount / totalDevicesDisplay) * 100) : 0;

  const statusCounts = useMemo(() => {
    if (useDistribution && distributionData?.statusCounts) return distributionData.statusCounts;
    if (open && devicesData.length) {
      const counts = {};
      STATUS_ORDER.forEach((s) => { counts[s] = 0; });
      devicesData.forEach((d) => {
        const s = d.deviceStatusName || "Depo";
        if (counts[s] != null) counts[s] += 1;
      });
      return counts;
    }
    return null;
  }, [useDistribution, distributionData?.statusCounts, open, devicesData]);

  const deviceTypeByStatus = useMemo(() => {
    const out = {};
    STATUS_ORDER.forEach((s) => { out[s] = []; });

    if (useDistribution && distributionData?.distributionMatrix) {
      const matrix = distributionData.distributionMatrix.matrix || [];
      const deviceTypes = distributionData.distributionMatrix.deviceTypes || [];
      const summaryByLabel = {};
      (distributionData.deviceTypeSummary || []).forEach((s) => {
        if (s?.deviceName != null) summaryByLabel[s.deviceName] = { hasGrinder: s.hasGrinder, cupNumber: s.cupNumber };
      });
      matrix.forEach((row) => {
        if (row.status === "Toplam") return;
        const statusKey = STATUS_ORDER.includes(row.status) ? row.status : null;
        if (!statusKey) return;
        deviceTypes.forEach((type) => {
          const count = row[type];
          if (count > 0) {
            const meta = summaryByLabel[type];
            out[statusKey].push({ deviceName: type, count, hasGrinder: meta?.hasGrinder, cupNumber: meta?.cupNumber });
          }
        });
        out[statusKey].sort((a, b) => b.count - a.count || String(a.deviceName).localeCompare(b.deviceName));
      });
      return out;
    }

    if (open && devicesData.length) {
      const normalize = (s) => STATUS_ORDER.find((c) => c.toLowerCase() === String(s || "").toLowerCase()) || s;
      devicesData.forEach((device) => {
        const statusKey = normalize(device.deviceStatusName);
        const deviceName = getDeviceDisplayName(device);
        const cupNumber = getDeviceCupNumber(device);
        const hasGrinder = deviceHasGrinder(device);
        let base = deviceName;
        if (deviceName.startsWith("Çay-FiltreKahve")) base = `${cupNumber}'li Çayset`;
        else if (deviceName.includes("Çayset-Fincanda")) base = `${cupNumber}'li Çayset-Fincanda`;
        else if (deviceName.includes("Fincanda")) base = `${cupNumber}'li Fincanda`;
        else if (deviceName.includes("Çayset")) base = `${cupNumber}'li Çayset`;
        else base = `${cupNumber}'li ${deviceName}`;
        if (base.toLowerCase().includes("espresso")) return;
        base = normalizeInvalidDeviceLabel(base);
        const label = base + (hasGrinder ? " (Değirmenli)" : " (Değirmensiz)");
        const arr = out[statusKey];
        if (!arr) return; // dağılım dışı statüler (ör. Üretim) atlanır
        const existing = arr.find((x) => x.deviceName === label);
        if (existing) existing.count += 1;
        else arr.push({ deviceName: label, count: 1, hasGrinder });
      });
      STATUS_ORDER.forEach((s) => {
        out[s].sort((a, b) => b.count - a.count || String(a.deviceName).localeCompare(b.deviceName));
      });
      return out;
    }
    return out;
  }, [useDistribution, distributionData?.distributionMatrix, distributionData?.deviceTypeSummary, open, devicesData]);

  const deviceTypeTotals = useMemo(() => {
    const sumByType = {};
    STATUS_ORDER.forEach((status) => {
      (deviceTypeByStatus[status] || []).forEach((item) => {
        const key = item.deviceName;
        if (!sumByType[key]) {
          let hasGrinder = item.hasGrinder;
          if (hasGrinder === undefined && typeof key === "string" && key.includes("|")) {
            hasGrinder = key.split("|")[2] === "true";
          }
          sumByType[key] = { deviceName: key, count: 0, hasGrinder, cupNumber: item.cupNumber };
        }
        sumByType[key].count += item.count;
      });
    });
    return Object.values(sumByType).sort((a, b) => b.count - a.count || String(a.deviceName).localeCompare(b.deviceName));
  }, [deviceTypeByStatus]);

  // Bir tip etiketinden model adını çıkar (3 model: Çayset / Fincanda / Çayset-Fincanda)
  const getModelFromLabel = (label) => {
    const s = String(label || "");
    if (/çayset-fincanda/i.test(s)) return "Çayset-Fincanda";
    if (/fincanda/i.test(s)) return "Fincanda";
    if (/çayset/i.test(s)) return "Çayset";
    return "Diğer";
  };

  // Mevcut veriden seçilebilir göz sayıları
  const cupOptions = useMemo(() => {
    const set = new Set();
    deviceTypeTotals.forEach((item) => {
      const cup = item.cupNumber ?? getCupFromDeviceLabel(item.deviceName);
      if (cup != null && Number(cup) > 0) set.add(Number(cup));
    });
    return [...set].sort((a, b) => a - b);
  }, [deviceTypeTotals]);

  // Filtre — genel toplam VE duruma göre bölümlerin hepsini etkiler
  const hasActiveFilter =
    totalsFilter.model !== "all" ||
    totalsFilter.grinder !== "all" ||
    totalsFilter.cup !== "all";

  const matchesFilter = (item) => {
    if (totalsFilter.model !== "all" && getModelFromLabel(item.deviceName) !== totalsFilter.model) {
      return false;
    }
    if (totalsFilter.grinder !== "all") {
      const has = item.hasGrinder === true;
      if (totalsFilter.grinder === "grinder" ? !has : has) return false;
    }
    if (totalsFilter.cup !== "all") {
      const cup = item.cupNumber ?? getCupFromDeviceLabel(item.deviceName);
      if (Number(cup) !== Number(totalsFilter.cup)) return false;
    }
    return true;
  };

  const filteredTypeTotals = useMemo(
    () => deviceTypeTotals.filter(matchesFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceTypeTotals, totalsFilter]
  );

  // Duruma göre tip listeleri — filtre uygulanmış
  const filteredDeviceTypeByStatus = useMemo(() => {
    const out = {};
    STATUS_ORDER.forEach((s) => {
      out[s] = (deviceTypeByStatus[s] || []).filter(matchesFilter);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceTypeByStatus, totalsFilter]);

  // Durum kartı sayıları — filtre aktifken filtrelenmiş tiplerin toplamı
  const displayStatusCounts = useMemo(() => {
    if (!hasActiveFilter) return statusCounts;
    const counts = {};
    STATUS_ORDER.forEach((s) => {
      counts[s] = (filteredDeviceTypeByStatus[s] || []).reduce(
        (sum, item) => sum + (item.count || 0),
        0
      );
    });
    return counts;
  }, [hasActiveFilter, statusCounts, filteredDeviceTypeByStatus]);

  if (!mounted) return null;

  const renderTypeCard = (item, serials, titlePrefix = "") => {
    const base = item.deviceName;
    const grinderLabel = item.hasGrinder === true ? "Değirmenli" : "Değirmensiz";
    const cupFromKey = typeof item.deviceName === "string" && item.deviceName.includes("|") ? item.deviceName.split("|")[1] : null;
    const cupNumber = item.cupNumber ?? cupFromKey ?? getCupFromDeviceLabel(base);
    const cupLabel = cupNumber != null && cupNumber !== "" ? formatCupLabel(cupNumber) : null;
    // Başlıkta göz öneki ve değirmen soneki gösterilmez — bu bilgiler zaten
    // alttaki çiplerde var. Gruplama anahtarı (item.deviceName) granular kalır.
    const displayName = String(base)
      .replace(/^\d+'l[iü]\s+/, "")
      .replace(/\s*\((Değirmenli|Değirmensiz)\)\s*$/, "");

    return (
      <button
        type="button"
        key={`${titlePrefix}${item.deviceName}`}
        onClick={() => setSerialsModal({ title: `${titlePrefix}${displayName} - Seri Numaraları`, serials, grinderLabel, cupLabel })}
        className="rounded-lg border border-gray-200/80 bg-white/80 px-3 py-2 flex flex-col gap-1.5 shadow-sm text-left hover:border-gray-300 hover:shadow-md transition-all cursor-pointer"
      >
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-800 truncate">{displayName}</span>
          <span className="shrink-0 inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-md bg-gray-700 text-white text-sm font-bold">
            ×{item.count}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {grinderLabel && (
            <span className={`inline-flex w-fit text-xs font-medium px-2 py-0.5 rounded ${grinderLabel === "Değirmenli" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
              {grinderLabel}
            </span>
          )}
          {cupLabel && (
            <span className="inline-flex w-fit text-xs font-medium px-2 py-0.5 rounded bg-sky-100 text-sky-700">{cupLabel}</span>
          )}
        </div>
      </button>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="xl"
      headerGradient
      title="Cihaz Dağılımı"
      subtitle={`${totalDevicesDisplay || 0} cihaz · durum ve tip kırılımı`}
      titleIcon={<FaChartBar className="w-5 h-5" />}
    >
      {/* Filtre bandı — sabit (scroll'da kaybolmaz); tüm bölümleri etkiler */}
      {!distributionLoading && (
        <div className={`shrink-0 px-4 py-3 border-b transition-colors ${hasActiveFilter ? "bg-indigo-50/60 border-indigo-200" : "bg-white border-gray-200"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Filtreler</span>
            {hasActiveFilter && (
              <button
                type="button"
                onClick={() => setTotalsFilter({ model: "all", grinder: "all", cup: "all" })}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                Temizle
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
            {[
              {
                key: "model",
                label: "Model",
                options: [
                  { id: "all", text: "Tümü" },
                  { id: "Çayset", text: "Çayset" },
                  { id: "Fincanda", text: "Fincanda" },
                  { id: "Çayset-Fincanda", text: "Çayset-Fincanda" },
                ],
              },
              {
                key: "grinder",
                label: "Değirmen",
                options: [
                  { id: "all", text: "Tümü" },
                  { id: "grinder", text: "Değirmenli" },
                  { id: "noGrinder", text: "Değirmensiz" },
                ],
              },
              {
                key: "cup",
                label: "Göz",
                options: [
                  { id: "all", text: "Tümü" },
                  ...cupOptions.map((c) => ({ id: String(c), text: `${c} göz` })),
                ],
              },
            ].map((group) => (
              <div key={group.key} className="flex items-center gap-1.5">
                <span className="text-xs font-semibold text-gray-500 shrink-0">{group.label}:</span>
                <div className="flex flex-wrap gap-1">
                  {group.options.map((opt) => {
                    const isActive = String(totalsFilter[group.key]) === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setTotalsFilter((p) => ({ ...p, [group.key]: opt.id }))}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                          isActive
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-md scale-105"
                            : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                        }`}
                      >
                        {opt.text}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Drawer.Body className="bg-gray-50/80">
          <div className="p-4 space-y-4 sm:space-y-5">
            {distributionLoading && (
              <div className="relative">
                <MascotLoadingOverlay message="Cihaz dağılımı yükleniyor..." />
                <div className="space-y-6 opacity-40">
                  {/* Özet kartları skeleton */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gray-200 animate-pulse shrink-0" />
                        <div className="space-y-1.5 flex-1">
                          <div className="h-5 w-12 bg-gray-200 rounded animate-pulse" />
                          <div className="h-3 w-20 bg-gray-100 rounded animate-pulse" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Durum kartları skeleton */}
                  <div>
                    <div className="h-4 w-44 bg-gray-200 rounded animate-pulse mb-3" />
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gray-200 animate-pulse shrink-0" />
                          <div className="space-y-1.5 flex-1">
                            <div className="h-5 w-10 bg-gray-200 rounded animate-pulse" />
                            <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Tip kartları skeleton */}
                  <div>
                    <div className="h-4 w-64 bg-gray-200 rounded animate-pulse mb-3" />
                    <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {Array.from({ length: 8 }).map((_, i) => (
                          <div key={i} className="rounded-lg border border-gray-200 bg-white px-3 py-2 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="h-3.5 w-24 bg-gray-200 rounded animate-pulse" />
                              <div className="h-6 w-8 bg-gray-200 rounded-md animate-pulse shrink-0" />
                            </div>
                            <div className="flex gap-1.5">
                              <div className="h-4 w-16 bg-gray-100 rounded animate-pulse" />
                              <div className="h-4 w-12 bg-gray-100 rounded animate-pulse" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {!distributionLoading && (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { val: totalDevicesDisplay, label: "Toplam Cihaz", Icon: FaLayerGroup, c: "violet" },
                    { val: onlineCount, label: "Çevrimiçi", Icon: FaNetworkWired, c: "emerald" },
                    { val: `%${statusPercent}`, label: "Durum", Icon: FaChartBar, c: "amber" },
                  ].map((s) => (
                    <div key={s.label} className={`rounded-xl bg-${s.c}-50/80 border border-${s.c}-100 p-4 flex items-center gap-3`}>
                      <div className={`p-2 rounded-lg bg-${s.c}-100`}>
                        <s.Icon className={`w-5 h-5 text-${s.c}-600`} />
                      </div>
                      <div>
                        <div className={`text-xl font-bold text-${s.c}-900`}>{s.val}</div>
                        <div className={`text-sm font-medium text-${s.c}-600`}>{s.label}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Status distribution */}
                {statusCounts && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2.5">
                      <div className="p-1.5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow-sm border border-indigo-100">
                        <FaChartBar className="w-4 h-4 text-indigo-600" />
                      </div>
                      <h4 className="text-sm sm:text-base font-bold text-gray-900">Duruma göre dağılım</h4>
                    </div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {STATUS_ORDER.map((status) => {
                        const config = STATUS_CONFIG[status];
                        const Icon = config?.icon || FaLayerGroup;
                        const count = displayStatusCounts?.[status] ?? 0;
                        const c = config?.color || "purple";
                        return (
                          <div key={status} className={`rounded-xl border p-4 flex items-center gap-3 ${STYLES[c]}`}>
                            <div className={`p-2 rounded-lg ${ICON_STYLES[c]}`}><Icon className="w-5 h-5" /></div>
                            <div>
                              <div className="text-xl font-bold">{count}</div>
                              <div className="text-sm font-medium opacity-90">{status}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* General totals */}
                {deviceTypeTotals.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2.5">
                      <div className="p-1.5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow-sm border border-indigo-100">
                        <FaLayerGroup className="w-4 h-4 text-indigo-600" />
                      </div>
                      <h4 className="text-sm sm:text-base font-bold text-gray-900">Genel toplam</h4>
                      <span className="text-xs text-gray-400 font-medium">durumdan bağımsız</span>
                    </div>
                    <div className="p-4">
                      {filteredTypeTotals.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">Seçilen filtrelere uyan cihaz yok.</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                          {filteredTypeTotals.map((item) => renderTypeCard(item, serialNumbersByType[item.deviceName] || []))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* By status */}
                <div>
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="p-1.5 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow-sm border border-indigo-100">
                      <FaCogs className="w-4 h-4 text-indigo-600" />
                    </div>
                    <h4 className="text-sm sm:text-base font-bold text-gray-900">Hangi cihazdan kaç tane</h4>
                    <span className="text-xs text-gray-400 font-medium">duruma göre</span>
                  </div>
                  <div className="space-y-4">
                    {STATUS_ORDER.map((status) => {
                      const items = filteredDeviceTypeByStatus[status] || [];
                      if (items.length === 0) return null;
                      const config = STATUS_CONFIG[status];
                      const Icon = config?.icon || FaLayerGroup;
                      const c = config?.color || "purple";
                      return (
                        <div key={status} className={`rounded-xl border p-4 ${BORDER_COLORS[c]}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <Icon className="w-4 h-4 opacity-80" />
                            <span className="font-semibold text-gray-800">{status}</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                            {items.map((item) => renderTypeCard(item, serialNumbersByStatusAndType[status]?.[item.deviceName] || [], `${status} - `))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Serials modal */}
                {serialsModal && (
                  <>
                    <div className="fixed inset-0 bg-black/40 z-[60]" onClick={() => setSerialsModal(null)} aria-hidden="true" />
                    <div className="fixed left-1/2 top-[8vh] -translate-x-1/2 w-[90vw] max-w-md max-h-[84vh] bg-white rounded-xl shadow-xl z-[61] flex flex-col border border-gray-200">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
                        <div className="min-w-0 pr-2">
                          <h4 className="text-sm font-bold text-gray-800 truncate">{serialsModal.title}</h4>
                          {(serialsModal.grinderLabel || serialsModal.cupLabel) && (
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {serialsModal.grinderLabel && (
                                <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded ${serialsModal.grinderLabel === "Değirmenli" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                                  {serialsModal.grinderLabel}
                                </span>
                              )}
                              {serialsModal.cupLabel && (
                                <span className="inline-flex text-xs font-medium px-2 py-0.5 rounded bg-sky-100 text-sky-700">{serialsModal.cupLabel}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <button type="button" onClick={() => setSerialsModal(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 shrink-0" aria-label="Kapat">
                          <FaTimes className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="p-4 overflow-y-auto flex-1 min-h-0">
                        {serialsModal.serials.length === 0 ? (
                          <p className="text-sm text-gray-500">Seri numarası bulunamadı.</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {serialsModal.serials.map((sn) => (
                              <li key={sn} className="text-sm font-mono text-gray-800 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">{sn}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
      </Drawer.Body>
    </Drawer>
  );
}
